/**
 * Known webmail providers mapped to their inbox URL, keyed by email domain
 * (lowercase, without the `@`). Used to deep-link "Open email app" straight
 * into the user's inbox instead of a blank `mailto:` compose window.
 */
const EMAIL_PROVIDER_INBOX_URL: Record<string, string> = {
  "gmail.com": "https://mail.google.com/mail/u/0/#inbox",
  "googlemail.com": "https://mail.google.com/mail/u/0/#inbox",
  "outlook.com": "https://outlook.live.com/mail/0/inbox",
  "hotmail.com": "https://outlook.live.com/mail/0/inbox",
  "live.com": "https://outlook.live.com/mail/0/inbox",
  "msn.com": "https://outlook.live.com/mail/0/inbox",
  "yahoo.com": "https://mail.yahoo.com/",
  "ymail.com": "https://mail.yahoo.com/",
  "icloud.com": "https://www.icloud.com/mail",
  "me.com": "https://www.icloud.com/mail",
  "mac.com": "https://www.icloud.com/mail",
  "aol.com": "https://mail.aol.com/",
  "protonmail.com": "https://mail.proton.me/u/0/inbox",
  "proton.me": "https://mail.proton.me/u/0/inbox",
  "zoho.com": "https://mail.zoho.com/",
};

/**
 * Resolves the webmail inbox URL for a known provider based on an email
 * address's domain, so "Open email app" can deep-link straight to the inbox.
 *
 * @param email - The recipient's email address (may be empty/undefined).
 * @returns The provider's inbox URL, or `undefined` when the domain isn't a
 *   recognised webmail provider — callers should fall back to `mailto:`.
 * @example
 * getEmailProviderInboxUrl("user@gmail.com"); // "https://mail.google.com/mail/u/0/#inbox"
 * getEmailProviderInboxUrl("user@company.com"); // undefined
 */
export function getEmailProviderInboxUrl(
  email: string | undefined
): string | undefined {
  const domain = email?.split("@")[1]?.toLowerCase().trim();
  if (!domain) {
    return undefined;
  }
  return EMAIL_PROVIDER_INBOX_URL[domain];
}
